import React from "react";
import { motion } from "framer-motion";
import { MapPin, Users, Utensils, Wine, Building2, Sparkles } from "lucide-react";
import { GUIDE_IMAGES, pickImageForContent } from "@/lib/images";

const ZONE_IMAGE = {
  "Pacific": GUIDE_IMAGES.aerial_resort,
  "Cabo San Lucas": GUIDE_IMAGES.marina,
  "Corridor": GUIDE_IMAGES.beach_palms,
  "San José del Cabo": GUIDE_IMAGES.art_district,
  "East Cape": GUIDE_IMAGES.sailing,
  "Unique Venue": GUIDE_IMAGES.farm_dinner,
};

function imageForVenue(v) {
  // Prefer tag-aware match, fall back to zone image
  const tagText = (v.tags || []).join(" ");
  const byTag = pickImageForContent(`${v.name} ${tagText} ${v.why || ""}`);
  if (byTag) return byTag;
  return ZONE_IMAGE[v.zone] || GUIDE_IMAGES.banquet_ocean;
}

function fmt(val) {
  if (!val || val === "—") return "—";
  // Strip common unit suffixes the LLM may inline (we add our own label).
  const cleaned = String(val)
    .replace(/\s*sq\.?\s*ft\.?/i, "")
    .replace(/\s*sqft/i, "")
    .replace(/\s*m²/i, "")
    .trim();
  // Numeric formatting with thousands separator when possible
  const n = Number(cleaned.replace(/[, ]/g, ""));
  if (!Number.isNaN(n) && n > 0) return n.toLocaleString();
  return cleaned || val;
}

export default function VenueCard({ data }) {
  const img = imageForVenue(data);
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="my-3 rounded-2xl overflow-hidden border border-[#e4e9f0] bg-white max-w-[460px] shadow-[0_8px_24px_rgba(36,47,68,0.08)]"
      data-testid="venue-card"
    >
      <div className="relative h-36 overflow-hidden">
        <img
          src={img.src}
          alt={data.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#242f44]/80 via-[#242f44]/15 to-transparent" />
        <div className="absolute left-3 top-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur text-[#242f44] text-[11px] font-semibold px-2.5 py-1 rounded-full">
          <MapPin size={11} className="text-[#faa200]" />
          {data.zone || "Los Cabos"}
        </div>
        <div className="absolute left-3 right-3 bottom-3">
          <div className="font-display text-[17px] md:text-[18px] text-white leading-tight drop-shadow-sm">
            {data.name}
          </div>
        </div>
      </div>

      <div className="px-4 py-3.5">
        {data.why && (
          <p className="text-[13.5px] leading-relaxed text-[#3a4867]">
            <span className="inline-flex items-center gap-1 text-[#faa200] mr-1 align-middle">
              <Sparkles size={13} />
            </span>
            {data.why}
          </p>
        )}

        <dl className="mt-3 grid grid-cols-4 gap-2 text-center">
          <Stat icon={<Users size={13} />} label="Rooms" value={fmt(data.rooms)} />
          <Stat icon={<Building2 size={13} />} label="Theater" value={fmt(data.theater)} />
          <Stat icon={<Utensils size={13} />} label="Banquet" value={fmt(data.banquet)} />
          <Stat icon={<Wine size={13} />} label="Cocktail" value={fmt(data.cocktail)} />
        </dl>

        {data.ballroom && data.ballroom !== "—" && (
          <div className="mt-2.5 text-[12px] text-[#4c5b76] bg-[#f7f1e7] border border-[#eee5d1] rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5">
            <Building2 size={12} className="text-[#faa200]" />
            Main ballroom <strong className="font-semibold text-[#242f44]">{fmt(data.ballroom)} sq.ft.</strong>
          </div>
        )}

        {data.tags && data.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.tags.map((tag) => (
              <span
                key={`tag-${tag}`}
                className="text-[11px] font-medium text-[#242f44] bg-white border border-[#e4e9f0] rounded-full px-2.5 py-0.5"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
}

function Stat({ icon, label, value }) {
  const empty = value === "—";
  return (
    <div className="rounded-xl bg-[#f7f9fc] border border-[#e4e9f0] px-1.5 py-2">
      <div className="flex items-center justify-center text-[#faa200] mb-0.5">
        {icon}
      </div>
      <div className={`font-display text-[14px] leading-none ${empty ? "text-[#b4bbca]" : "text-[#242f44]"}`}>
        {value}
      </div>
      <div className="mt-0.5 text-[10.5px] uppercase tracking-wider text-[#7a879e]">
        {label}
      </div>
    </div>
  );
}
